import React, { ChangeEvent, useEffect, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { Card } from 'components'
import { useFile, useLoading, useModal, usePersistForm, useToggle } from 'hooks'
import moment from 'moment'
import { ViewContractModal } from 'pages/NGT/CRE/components/ViewContractModal/ViewContractModal'
import { useForm } from 'react-hook-form'
import { RegisterPendingServices } from 'services/NGT/RegisterPendingItems'
import {
  IWorkingDocumentationRequest,
  IWorkingDocumetationResponse,
  WorkingDocumentationService
} from 'services/NGT/WorkingDocumentation'
import { DocumentServices } from 'services/Supplier/Ged'
import { COLORS, FORMAT, SPACING, OLDYUP as YUP } from 'utils'
import { TipoPendencia } from 'utils/types'

import {
  FilterTableSearchDocumentation,
  TableSearchDocumentation
} from './components'

const DEFAULT_WORKING_DOCUMENTATION_REQUEST: IWorkingDocumentationRequest = {
  cdContrato: '',
  competenciaFim: null,
  competenciaInicio: null,
  cpf: '',
  idTipoDocumento: 0,
  nmEfetivo: '',
  nmFornecedor: '',
  pageNumber: 1,
  pageSize: 15
}

const SearchDocumentation: React.FC = () => {
  const { setLoading } = useLoading()
  const { showModal } = useModal()
  const { viewFile, downloadFile } = useFile()

  const [page, setPage] = useState<number>(1)
  const [pageCount, setPageCount] = useState<number>(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(15)
  const [openViewContractModal, toggleViewContractModal] = useToggle()
  const [selectedContractId, setSelectedContractId] = useState(0)
  const [wasClean, setWasClean] = useState<boolean>(false)

  const [tiposDocumento, setTiposDocumento] = useState<TipoPendencia[]>([])
  const [workingDocumentation, setWorkingDocumentation] = useState<
    IWorkingDocumetationResponse[]
  >([])

  const [workingDocmentationRequest, setWorkingDocumentationRequest] =
    useState<IWorkingDocumentationRequest>(
      DEFAULT_WORKING_DOCUMENTATION_REQUEST
    )

  const schema = YUP.dateRange(
    'competenciaInicio',
    'competenciaFim'
  ).oneRequireAnother

  const {
    reset,
    watch,
    setValue,
    trigger,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema)
  })

  usePersistForm('ngt_search_documentation', {
    setValue,
    watch
  })

  useEffect(() => {
    const subs = watch(() => trigger())
    return () => subs.unsubscribe()
  }, [watch])

  const handleSearchWorkingItems = () => {
    setLoading('loading')

    WorkingDocumentationService()
      .getPaginated({
        ...workingDocmentationRequest,
        competenciaFim: moment(
          workingDocmentationRequest.competenciaFim
        ).isValid()
          ? moment(workingDocmentationRequest.competenciaFim).toDate()
          : null,
        competenciaInicio: moment(
          workingDocmentationRequest.competenciaInicio
        ).isValid()
          ? moment(workingDocmentationRequest.competenciaInicio).toDate()
          : null,
        cpf: FORMAT.unMask(workingDocmentationRequest.cpf),
        idTipoDocumento:
          workingDocmentationRequest.idTipoDocumento >= 0
            ? workingDocmentationRequest.idTipoDocumento
            : 0,
        pageNumber: page,
        pageSize: rowsPerPage
      })
      .then((response) => {
        setWorkingDocumentation(response.data.items)
        setPageCount(response.data.pageCount)
      })
      .catch(() => {
        showModal({
          hideButtonCancel: true,
          message:
            'Não foi possível pesquisar lista de documentos, tente novamente!',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
        setLoading('idle')
      })
      .finally(() => setLoading('idle'))
  }

  const areDatesValid = () => {
    if (
      moment(workingDocmentationRequest.competenciaInicio).diff(
        moment(workingDocmentationRequest.competenciaFim)
      ) > 0 ||
      (moment(workingDocmentationRequest.competenciaInicio).isValid() &&
        !moment(workingDocmentationRequest.competenciaFim).isValid()) ||
      (!moment(workingDocmentationRequest.competenciaInicio).isValid() &&
        moment(workingDocmentationRequest.competenciaFim).isValid())
    ) {
      return false
    }

    return (
      moment(
        workingDocmentationRequest.competenciaInicio || moment()
      ).isValid() &&
      moment(workingDocmentationRequest.competenciaFim || moment()).isValid()
    )
  }

  const handleViewContractModal = (contractId: number) => {
    setSelectedContractId(contractId)
    toggleViewContractModal()
  }
  const handleClearForm = () => {
    setWorkingDocumentationRequest(DEFAULT_WORKING_DOCUMENTATION_REQUEST)
    reset()
    setWasClean(true)
  }

  const handleGetTiposDocumento = () => {
    RegisterPendingServices()
      .getAll({isTelaCRE:true})
      .then((response) => {
        if (response.status === 200) {
          setTiposDocumento(response.data)
        }
      })
      .catch(() => {
        showModal({
          hideButtonCancel: true,
          message: 'Não foi possível obter os arquivos de documentação',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
      })
  }

  useEffect(() => {
    handleGetTiposDocumento()
    handleSearchWorkingItems()
  }, [])

  const handleOnClickSearch = () => {
    if (page === 1) {
      handleSearchWorkingItems()
      return
    }

    setPage(1)
  }

  useEffect(() => {
    handleSearchWorkingItems()
  }, [page, rowsPerPage])

  useEffect(() => {
    if (wasClean) {
      if (page === 1) {
        handleSearchWorkingItems()
      } else {
        setPage(1)
      }
      setWasClean(false)
    }
  }, [wasClean])

  const handleOnChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setWorkingDocumentationRequest((oldValues) => ({
      ...oldValues,
      [event.target.name]: event.target.value
    }))
    setValue(event.target.name, event.target.value)
  }

  const handleChangePage = (page: number) => setPage(page + 1)

  const handleRowsPerPage = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    handleChangePage(0)
  }

  const handleViewDocument = (externalId: number) => {
    setLoading('loading')

    DocumentServices()
      .SearchDocument(externalId)
      .then((response) => {
        if (response.status === 200) {
          const { file: base64, dsArquivoOriginal: filename } =
            response.data.versaoDocumentoInfo

          viewFile({
            local: {
              base64,
              filename
            }
          })
        }

        setLoading('idle')
      })
      .catch(() => {
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: 'Houve um problema ao visualizar o arquivo',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
      })
  }

  return (
    <Card
      marginLeft={SPACING.spacing20}
      marginRight={SPACING.spacing20}
      marginBottom={SPACING.spacing100}
      padding={SPACING.spacing20}
      boxShadow={`0 0 1em ${COLORS.lightGray}`}
    >
      <FilterTableSearchDocumentation
        handlePesquisar={handleOnClickSearch}
        handleClearForm={handleClearForm}
        handleOnChange={handleOnChange}
        areDatesValid={areDatesValid}
        tiposDocumento={tiposDocumento}
        workingDocumentation={workingDocmentationRequest}
        errors={errors}
      />
      <TableSearchDocumentation
        selectedContractInfo={handleViewContractModal}
        areDatesValid={areDatesValid}
        tableData={workingDocumentation}
        pageCount={pageCount}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleRowsPerPage}
        page={page}
        rowsPerPage={rowsPerPage}
        handleViewDocument={handleViewDocument}
        downloadFile={downloadFile}
      />
      <ViewContractModal
        showModal={openViewContractModal}
        toggleModal={toggleViewContractModal}
        contractID={selectedContractId}
      />
    </Card>
  )
}

export default SearchDocumentation
